import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import {
	type IncomingInvoiceDeliveryNoteLinkSchema,
	incomingInvoiceDeliveryNoteLinkSchema,
} from "@msuite/utils/schemas";
import type { IncomingInvoiceDeliveryNoteListItem } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { IncomingInvoiceLinkForm } from "@/forms/incoming-invoice-link-form";
import { client } from "@/hono";

/** Props Interface */
interface LinkDeliveryNoteInvoiceModalProps extends ModalProps {
	deliveryNote: IncomingInvoiceDeliveryNoteListItem;
}

/** Link a delivery note to the invoice that bills it, or — emptied — take a wrong link back. */
export function LinkDeliveryNoteInvoiceModal({
	isOpen,
	onClose,
	deliveryNote,
}: LinkDeliveryNoteInvoiceModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const methods = useForm<IncomingInvoiceDeliveryNoteLinkSchema>({
		defaultValues: { incomingInvoiceId: deliveryNote.invoice?.id ?? null },
		resolver: resolver(incomingInvoiceDeliveryNoteLinkSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: IncomingInvoiceDeliveryNoteLinkSchema) {
		const response = await client.incoming_invoices["delivery-notes"][":deliveryNoteId"].$patch({
			param: { deliveryNoteId: deliveryNote.id },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["incoming-invoices"]) });
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("incoming_invoices.delivery_notes.link_invoice")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<p className="label">
						{t("incoming_invoices.delivery_notes.link_invoice_hint", {
							number: deliveryNote.number ?? "–",
						})}
					</p>
					<IncomingInvoiceLinkForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
